import _ from 'lodash'
import { MutationTree } from 'vuex'
import { BlogState } from '@/modules/blog/store'
import {Post, PostTranslation, PostPagination, PostPaginationFilters, Tag} from '@/modules/blog/types'

export enum MutationTypes {
    SetEditingPost = 'setEditingPost',
    SetEditingPostTranslation = 'setEditingPostTranslation',
    SetEditingPostCurrentTranslation = 'setEditingPostCurrentTranslation',
    SetCurrentPostPagination = 'setCurrentPostPagination',
    SetCurrentPostPaginationFilters = 'setCurrentPostPaginationFilters',
    MergePostPaginationFilters = 'MERGE_POST_PAGINATION_FILTERS',
    SetPostPaginationLoading = 'setPostPaginationLoading',
    ShowPostDeletionConfirm = 'showPostDeletionConfirm',
    SetPostToDelete = 'setPostToDelete',
    DeletePostInCurrentPagination = 'deletePostInCurrentPagination',
    ActivePostPreview = 'post/preview/active',
    SetCurrentPostPreview = 'post/preview/set',
    SetLoadingPreview = 'post/preview/loading/set',
    ShowSaveConfirm = 'post/save/confirm/show',
    UpdateCurrentPostField = 'post/current/field/update',
    DeletePostTranslation = 'DELETE_POST_TRANSLATION',
    // Mutaciones para etiquetas
    SetTagList = 'SET_TAG_LIST',
    SetCurrentTag = 'SET_CURRENT_TAG',
    DeleteTag = 'DELETE_TAG',
    UpdateTagField = 'UPDATE_TAG_FIELD',
    AddTag = 'ADD_TAG',
    ToggleTagsFetched = 'TOGGLE_TAG_FETCHED',
}

const mutations: MutationTree<BlogState> = {

    /**
     * Define la publicación que esta actualmente en edición
     */
    [MutationTypes.SetEditingPost]: (state, post: Post) => {
        state.editingPost = post
    },

    /**
     * Registra una traducción en la publicación actualmente en edición.
     * Si ya existe una traducción con el mismo identificador
     * regional, se sobre-escribirá.
     */
    [MutationTypes.SetEditingPostTranslation]: (state, translation: PostTranslation | string) => {
        translation = typeof translation === 'string' ? new PostTranslation(translation) : translation
        const key = state.editingPost.translations.findIndex(t => t.locale === (translation as PostTranslation).locale)
        if (key > -1) {
            state.editingPost.translations.splice(key, 1, translation)
            return
        }

        state.editingPost.translations.push(translation)
    },

    /**
     * Define el localismo de la traducción actual
     */
    [MutationTypes.SetEditingPostCurrentTranslation]: (state, locale: string) => {
        state.currentTranslation = locale
    },

    /**
     * Registra en el estado la paginación actualmente mostrada al usuario.
     */
    [MutationTypes.SetCurrentPostPagination]: (state, pagination: PostPagination) => {
        state.currentPostPagination = pagination
    },
    /**
     * Actualiza el objeto que almacena los filtros para la paginación de
     * publicaciones
     *
     * @param state
     * @param filters
     */
    [MutationTypes.SetCurrentPostPaginationFilters]: (state, filters: PostPaginationFilters) => {
        state.currentPostPaginationFilters = filters
    },

    [MutationTypes.SetPostPaginationLoading]: (state, isLoading: boolean) => {
        state.postPaginationLoading = isLoading
    },

    [MutationTypes.ShowPostDeletionConfirm]: (state, show: boolean) => {
        state.showPostDeletionConfirm = show
    },

    [MutationTypes.SetPostToDelete]: (state, post: Post) => {
        state.postToDelete = post
    },

    /**
     * Elimina un articulo de la paginación actual buscandolo por su indentificado
     */
    [MutationTypes.DeletePostInCurrentPagination]: (state, post: Post): boolean => {
        const pagination = state.currentPostPagination
        const index = pagination.posts.findIndex(p => p.id === post.id)
        if (index >= 0) {
            pagination.posts.splice(index, 1)

            return true
        }

        return false
    },

    /**
     * Activa o desativa la vista previa de la publicación
     */
    [MutationTypes.ActivePostPreview]: (state, active: boolean) => state.postPreviewActive = active,

    [MutationTypes.SetCurrentPostPreview]: (state, preview: string) => state.currentPostPreview = preview,

    [MutationTypes.SetLoadingPreview]: (state, loading: boolean) => state.loadingPreview = loading,

    [MutationTypes.ShowSaveConfirm]: (state, show: boolean) => state.saveConfirm = show,

    /**
     * Actualiza un campo de la publicación actual
     */
    [MutationTypes.UpdateCurrentPostField]: (state, payload: {path: string, value: any}) => {
        _.set(state, `editingPost.${payload.path}`, payload.value)
    },

    /**
     * Elimina una traducción de la publicación actualmente en edición por el usuario
     *
     * @param state
     * @param locale
     */
    [MutationTypes.DeletePostTranslation]: (state, locale: string) => {
        const index = state.editingPost.translations.findIndex(t => t.locale === locale)
        // La traducción no existe
        if (index === -1) {
            return
        }

        state.editingPost.translations.splice(index, 1)
    },
    /**
     * Establece las etiquetas registradas en la aplicación
     *
     * @param state
     * @param tags
     */
    [MutationTypes.SetTagList]: (state, tags: Tag[]) => state.tags = tags,

    /**
     * Establece la etiqueta que se encuentra actualmente seleccionada por el usuario
     * @param state
     * @param tag
     */
    [MutationTypes.SetCurrentTag]: (state, tag: Tag) => state.tag = _.cloneDeep(tag),

    /**
     * Elimina un etiqueta de la lista de etiquetas cargada en el estado de la aplicación
     *
     * @param state
     * @param tag
     */
    [MutationTypes.DeleteTag]: (state, tag: Tag) => {
        const index = state.tags.findIndex(t => t.id === tag.id)
        if (index === -1) {
            return
        }

        state.tags.splice(index, 1)
    },

    /**
     * Actualiza un campo de la traducción actualmente en edición
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateTagField]: (state, payload: {field: string, value: any}) => {
        _.set(state.tag, payload.field, payload.value)
    },

    /**
     * Añade una nueva etiqueta a la lista de etiquetas registradas en la aplicación
     *
     * @param state
     * @param payload
     */
    [MutationTypes.AddTag]: (state, tag: Tag) => {
        const index = state.tags.findIndex(t => t.id === tag.id)
        if (index === -1) {
            state.tags.push(tag)
        }

        state.tags.splice(index, 1, tag)
    },

    /**
     * Establece si la lista de etiquetas ya ha sido obtenida
     *
     * @param state
     * @param fetched
     */
    [MutationTypes.ToggleTagsFetched]: (state, fetched: boolean) => state.tagsFetched = fetched,
    /**
     * Fusiona los filtros de paginación argumentados con los actuales
     *
     * @param state
     * @param filters
     */
    [MutationTypes.MergePostPaginationFilters]: (state, filters: Partial<PostPaginationFilters>) => {
        Object.assign(state.currentPostPaginationFilters, filters)
    },
}

export default mutations
